import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useOutletContext } from 'react-router-dom';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { Barcode, Camera as CameraIcon, ScanFace, Lock, Search, Users, CheckCircle2 } from 'lucide-react';
import { API, auth, inputCls, PageHead, errMsg, exportPdf, exportExcel } from './finance/shared';
import { EmptyState } from '../../components/dashboard/TodaySchedule';
import Icon from '../../components/dashboard/DashboardIcons';
import { canUseWhatsApp } from '../../lib/whatsapp';
import { WhatsAppMessageModal } from '../../components/whatsapp/WhatsAppMessageModal';
import { subscriptionBadge, daysRemaining } from './players/subscriptionUtils';
import { getReportSettings, logExport } from '../../lib/reportStudioApi';
import { useClubIdentity } from '../../lib/useClubIdentity';
import ReportPrintFrame from '../../components/reportStudio/ReportPrintFrame';

const ProgressRing = ({ pct }) => {
  const r = 26; const c = 2 * Math.PI * r;
  const tone = pct >= 80 ? '#0d9488' : pct >= 50 ? '#f59e0b' : '#ef4444';
  return (
    <svg width="64" height="64" viewBox="0 0 64 64" className="-rotate-90">
      <circle cx="32" cy="32" r={r} fill="none" stroke="#e2e8f0" strokeWidth="6" />
      <circle cx="32" cy="32" r={r} fill="none" stroke={tone} strokeWidth="6" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c - (c * Math.min(pct, 100)) / 100}
        style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
    </svg>
  );
};

const SubscriptionInfo = ({ player, dark }) => {
  if (!player) return null;
  const badge = subscriptionBadge(player);
  const dl = daysRemaining(player.subscription_end);
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs" data-testid="scan-subscription-info">
      <span className={`rounded-full px-2 py-0.5 font-bold ${badge.cls}`}>{badge.label}</span>
      {badge.key === 'active' && dl !== null && (
        <span className={dark ? 'text-slate-300' : 'text-slate-500'}>
          {dl >= 0 ? `متبقي ${dl} ${dl === 1 ? 'يوم' : 'يوماً'} على الانتهاء` : 'منتهٍ'}
        </span>
      )}
    </div>
  );
};

const today = () => new Date().toISOString().slice(0, 10);

const beep = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine'; osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    osc.connect(gain); gain.connect(ctx.destination);
    osc.start(); osc.stop(ctx.currentTime + 0.12);
    osc.onended = () => ctx.close();
  } catch { /* noop */ }
};

const CameraScanner = ({ onScan, onClose }) => {
  const [error, setError] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [cameraId, setCameraId] = useState(null);
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [flash, setFlash] = useState(false);
  const [result, setResult] = useState(null); // {type: ok|dup|err, message, player}
  const scannerRef = useRef(null);
  const genRef = useRef(0);
  const chainRef = useRef(Promise.resolve());
  const resultRef = useRef(null);
  const lastRef = useRef({ code: null, at: 0 });
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  const setScanResult = (r) => { resultRef.current = r; setResult(r); };
  const scanAgain = () => {
    setScanResult(null);
    try { scannerRef.current?.resume(); } catch { /* noop */ }
  };
  const forceRegister = (code) => {
    Promise.resolve(onScanRef.current(code, true)).then((res) => {
      setScanResult(res || { type: 'ok', message: 'تم تسجيل الحضور' });
    }).catch(() => {
      setScanResult({ type: 'err', message: 'فشل تسجيل الحضور', player: null });
    });
  };

  const startCamera = useCallback(async (scanner, id, gen) => {
    try {
      await scanner.start(
        id || { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: (viewfinderWidth, viewfinderHeight) => {
            const size = Math.floor(Math.min(viewfinderWidth, viewfinderHeight) * 0.75);
            return { width: size, height: size };
          },
        },
        (text) => {
          if (genRef.current !== gen || resultRef.current) return;
          const now = Date.now();
          if (lastRef.current.code === text && now - lastRef.current.at < 4000) return;
          lastRef.current = { code: text, at: now };
          beep();
          if (navigator.vibrate) navigator.vibrate(120);
          setFlash(true);
          setTimeout(() => setFlash(false), 400);
          try { scanner.pause(true); } catch { /* noop */ }
          Promise.resolve(onScanRef.current(text)).then((res) => {
            if (genRef.current === gen) setScanResult(res || { type: 'ok', message: 'تم تسجيل الحضور' });
          }).catch(() => {
            if (genRef.current === gen) setScanResult({ type: 'err', message: 'فشل تسجيل الحضور', player: null });
          });
        },
        () => {}
      );
      if (genRef.current !== gen) {
        // نسخة قديمة من كاميرا سابقة (تأثير React StrictMode في وضع التطوير) — أوقفها فوراً بلا تحديث حالة
        try { await scanner.stop(); scanner.clear(); } catch { /* noop */ }
        return;
      }
      setError(null);
      try {
        const caps = scanner.getRunningTrackCameraCapabilities();
        const torch = caps?.torchFeature?.();
        setTorchSupported(Boolean(torch?.isSupported?.()));
      } catch { setTorchSupported(false); }
    } catch (err) {
      if (genRef.current === gen) setError('تعذر تشغيل الكاميرا — تأكد من السماح بالوصول للكاميرا. ' + (err?.message || ''));
    }
  }, []);

  useEffect(() => {
    const gen = ++genRef.current;
    let scanner = null;
    // ينتظر اكتمال إيقاف أي كاميرا سابقة (يحدث في وضع React.StrictMode للتطوير) قبل فتح كاميرا جديدة لمنع تعارض عنصر الفيديو
    chainRef.current = chainRef.current.then(async () => {
      if (genRef.current !== gen) return;
      scanner = new Html5Qrcode('camera-scan-region', {
        formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
      });
      scannerRef.current = scanner;
      Html5Qrcode.getCameras().then((list) => { if (genRef.current === gen) setCameras(Array.isArray(list) ? list : []); }).catch(() => {});
      await startCamera(scanner, null, gen);
    }).catch(() => {});
    return () => {
      chainRef.current = chainRef.current.then(async () => {
        if (!scanner) return;
        try { await scanner.stop(); scanner.clear(); } catch { /* noop */ }
      }).catch(() => {});
    };
  }, [startCamera]);

  const switchCamera = () => {
    if (cameras.length < 2) return;
    const idx = cameras.findIndex((c) => c.id === cameraId);
    const next = cameras[(idx + 1) % cameras.length];
    const scanner = scannerRef.current;
    const gen = ++genRef.current;
    setCameraId(next.id);
    setTorchOn(false);
    setScanResult(null);
    chainRef.current = chainRef.current.then(async () => {
      try { await scanner.stop(); } catch { /* noop */ }
      await startCamera(scanner, next.id, gen);
    }).catch(() => {});
  };

  const toggleTorch = async () => {
    const scanner = scannerRef.current;
    try {
      const caps = scanner.getRunningTrackCameraCapabilities();
      const torch = caps.torchFeature();
      if (torch.isSupported()) { await torch.apply(!torchOn); setTorchOn((v) => !v); }
    } catch { /* noop */ }
  };

  const toneMap = {
    teal: { ring: 'border-teal-400', icon: 'text-teal-400', badge: 'bg-teal-100 text-teal-800' },
    amber: { ring: 'border-amber-400', icon: 'text-amber-400', badge: 'bg-amber-100 text-amber-800' },
    red: { ring: 'border-red-400', icon: 'text-red-400', badge: 'bg-red-100 text-red-800' },
  };
  const tone = toneMap[result?.type === 'ok' ? 'teal' : result?.type === 'dup' ? 'amber' : 'red'];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="camera-scanner-modal">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-2xl bg-white p-4 shadow-2xl">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-900">قراءة رمز QR بالكاميرا</h3>
          <div className="flex items-center gap-1.5">
            {cameras.length > 1 && (
              <button onClick={switchCamera} title="تبديل الكاميرا" className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100" data-testid="camera-switch-btn"><Icon name="refresh" className="w-4 h-4" /></button>
            )}
            {torchSupported && (
              <button onClick={toggleTorch} title="الفلاش" className={`rounded-lg p-1.5 ${torchOn ? 'bg-amber-100 text-amber-700' : 'text-slate-500 hover:bg-slate-100'}`} data-testid="camera-torch-btn"><Icon name="bolt" className="w-4 h-4" /></button>
            )}
            <button onClick={onClose} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100" data-testid="camera-close-btn"><Icon name="x" className="w-4 h-4" /></button>
          </div>
        </div>
        {error ? <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="camera-error">{error}</div>
          : !result && <p className="mb-2 text-xs text-slate-500">وجّه الكاميرا نحو رمز QR في بطاقة اللاعب — سيُسجل الحضور تلقائياً مع صوت تأكيد ويمكنك مسح عدة بطاقات متتالية</p>}
        <div className="relative overflow-hidden rounded-xl bg-slate-900">
          <div id="camera-scan-region" className={`ring-4 transition-all duration-300 ${flash ? 'ring-teal-400' : 'ring-transparent'}`} />
          {result && (
            <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-slate-900/95 p-6 text-center backdrop-blur-sm" data-testid="scan-result-overlay">
              {result.player?.photo_url ? (
                <img src={result.player.photo_url} alt="" className={`h-16 w-16 rounded-full border-2 object-cover ${tone.ring}`} onError={(e) => { e.target.style.display = 'none'; }} />
              ) : (
                <div className={`flex h-16 w-16 items-center justify-center rounded-full border-2 bg-white/10 ${tone.ring}`}>
                  <Icon name={result.type === 'ok' ? 'check' : result.type === 'dup' ? 'clock' : 'x'} className={`h-7 w-7 ${tone.icon}`} />
                </div>
              )}
              {result.player?.name && <div className="text-base font-bold text-white" data-testid="scan-result-player-name">{result.player.name}</div>}
              <div className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${tone.badge}`} data-testid="scan-result-message">{result.message || result.msg}</div>
              <SubscriptionInfo player={result.player} dark />
              {result.type === 'confirm' ? (
                <div className="mt-1 flex gap-2">
                  <button onClick={() => forceRegister(result.code)} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-bold text-white transition-transform hover:scale-105" data-testid="camera-force-register-btn">تسجيل رغم الانتهاء</button>
                  <button onClick={scanAgain} className="rounded-lg bg-white px-4 py-2 text-sm font-bold text-slate-900 transition-transform hover:scale-105" data-testid="scan-again-btn">تجاهل</button>
                </div>
              ) : (
                <button onClick={scanAgain} className="mt-1 rounded-lg bg-white px-5 py-2 text-sm font-bold text-slate-900 transition-transform hover:scale-105" data-testid="scan-again-btn">قراءة جديدة</button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function AttendancePage() {
  const { club, user, selectedSeasonId } = useOutletContext() || {};
  const canWhatsApp = canUseWhatsApp(user);
  const [waAbsentPlayer, setWaAbsentPlayer] = useState(null);
  const [day, setDay] = useState(today());
  const [cameraOpen, setCameraOpen] = useState(false);
  const [catFilter, setCatFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [players, setPlayers] = useState([]);
  const [data, setData] = useState({ records: [], present: 0, total_active_players: 0 });
  const [loading, setLoading] = useState(true);
  const [scanValue, setScanValue] = useState('');
  const [manualPlayer, setManualPlayer] = useState('');
  const [search, setSearch] = useState('');
  const [feedback, setFeedback] = useState(null); // {type: ok|dup|err, msg}
  const scanRef = useRef(null);
  const feedbackTimer = useRef(null);
  const exportPrintRef = useRef(null);
  const [studio, setStudio] = useState(null);
  const identity = useClubIdentity();
  useEffect(() => { getReportSettings('attendance_report').then(setStudio).catch(() => {}); }, []);
  const showFeedback = (f) => {
    if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
    setFeedback(f);
    feedbackTimer.current = setTimeout(() => setFeedback(null), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ day });
    if (catFilter) params.set('category_id', catFilter);
    if (selectedSeasonId && selectedSeasonId !== 'all') params.set('season_id', selectedSeasonId);
    try {
      const res = await axios.get(`${API}/player-attendance?${params}`, auth());
      setData(res.data || { records: [] });
    } catch { setData({ records: [], present: 0, total_active_players: 0 }); }
    setLoading(false);
  }, [day, catFilter, selectedSeasonId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    (async () => {
      const g = (u) => axios.get(`${API}${u}`, auth()).then((r) => r.data).catch(() => []);
      const [c, p] = await Promise.all([g('/categories'), g('/players')]);
      setCategories(Array.isArray(c) ? c : []);
      setPlayers(Array.isArray(p) ? p.filter((x) => x.is_active) : []);
    })();
  }, []);
  useEffect(() => {
    const isTouchDevice = window.matchMedia('(pointer: coarse)').matches;
    if (!isTouchDevice) scanRef.current?.focus();
  }, [loading]);

  const registerCode = async (code, force = false) => {
    if (!code.trim()) return null;
    const trimmed = code.trim();
    try {
      const res = await axios.post(`${API}/player-attendance/scan`, { code: trimmed, session_date: day, force }, auth());
      if (res.data.status === 'subscription_expired') {
        const result = { type: 'confirm', msg: res.data.message, player: res.data.player || null, code: trimmed };
        if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
        setFeedback(result);
        return result;
      }
      const type = res.data.status === 'duplicate' ? 'dup' : 'ok';
      const message = res.data.message;
      showFeedback({ type, msg: message, player: res.data.player || null, code: trimmed });
      load();
      return { type, message, player: res.data.player || null, code: trimmed };
    } catch (err) {
      const message = errMsg(err, 'فشل تسجيل الحضور');
      showFeedback({ type: 'err', msg: message, player: null, code: trimmed });
      return { type: 'err', message, player: null, code: trimmed };
    }
  };

  const onScanSubmit = (e) => { e.preventDefault(); registerCode(scanValue); setScanValue(''); scanRef.current?.focus(); };
  const onManual = () => { if (manualPlayer) { registerCode(manualPlayer); setManualPlayer(''); } };
  const removeRecord = async (id) => {
    try { await axios.delete(`${API}/player-attendance/${id}`, auth()); load(); } catch { /* noop */ }
  };
  const confirmForceRegister = () => { if (feedback?.code) registerCode(feedback.code, true); };
  const dismissFeedback = () => setFeedback(null);

  const pct = data.total_active_players ? Math.round((data.present / data.total_active_players) * 100) : 0;
  const presentIds = new Set((data.records || []).map((r) => r.player_id));
  const absent = players.filter((p) => !presentIds.has(p.id) && (!catFilter || p.category_id === catFilter));
  const q = search.trim().toLowerCase();
  const visibleRecords = q ? (data.records || []).filter((r) => r.player_name?.toLowerCase().includes(q) || String(r.jersey_number ?? '').includes(q)) : (data.records || []);
  const visibleAbsent = q ? absent.filter((p) => p.name?.toLowerCase().includes(q)) : absent;
  const activeAttendees = visibleRecords.filter((r) => subscriptionBadge(r).key === 'active').length;
  const expiredAttendees = visibleRecords.filter((r) => subscriptionBadge(r).key === 'expired').length;
  const studioSettings = studio?.effective || {};
  const studioContent = studioSettings.content || {};
  const studioTemplateUsed = studio?.use_custom ? 'تخصيص مستقل' : null;

  const [pdfExporting, setPdfExporting] = useState(false);
  const handleExportPdf = async () => {
    setPdfExporting(true);
    try {
      await exportPdf(exportPrintRef.current, 'سجل-الحضور', { orientation: studioSettings.page?.orientation, paperSize: studioSettings.page?.paper_size });
      logExport('attendance_report', 'pdf', studioTemplateUsed, 'success', 'سجل-الحضور.pdf');
    } catch { logExport('attendance_report', 'pdf', studioTemplateUsed, 'failed', null); }
    finally { setPdfExporting(false); }
  };
  const handleExportExcel = () => {
    const rows = visibleRecords.map((r) => ({ 'اللاعب': r.player_name, 'الرقم': r.jersey_number ?? '', 'حالة الاشتراك': subscriptionBadge(r).label, 'الوقت': r.time, 'سجّله': r.recorded_by || '' }));
    exportExcel(rows, 'سجل-الحضور');
    logExport('attendance_report', 'excel', studioTemplateUsed, 'success', 'سجل-الحضور.xlsx');
  };

  return (
    <div className="mx-auto max-w-7xl space-y-5" data-testid="attendance-page">
      <PageHead title="الحضور والانصراف" desc="تسجيل حضور اللاعبين بمسح الباركود، الكاميرا، أو يدوياً" />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-600"><CheckCircle2 className="h-5 w-5" /></span>
          <div><div className="text-xs font-medium text-slate-500">الحاضرون</div><div className="text-2xl font-extrabold text-teal-700" data-testid="attendance-present">{data.present || 0}</div></div>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#16233b]/5 text-[#16233b]"><Users className="h-5 w-5" /></span>
          <div><div className="text-xs font-medium text-slate-500">اللاعبون النشطون</div><div className="text-2xl font-extrabold text-slate-900">{data.total_active_players || 0}</div></div>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
          <div className="relative flex h-16 w-16 shrink-0 items-center justify-center">
            <ProgressRing pct={pct} />
            <span className="absolute text-sm font-extrabold text-slate-900" data-testid="attendance-pct">{pct}%</span>
          </div>
          <div className="text-xs font-medium text-slate-500">نسبة الحضور<div className="text-[11px] text-slate-400">لهذا اليوم</div></div>
        </div>
      </div>

      <div>
        <div className="mb-2 flex items-center gap-2 text-sm font-bold text-[#16233b]"><Icon name="tag" className="w-4 h-4" /> طريقة تسجيل الحضور</div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-2xl border-2 border-teal-400 bg-teal-50/50 p-4 shadow-sm ring-1 ring-teal-100" data-testid="method-barcode-card">
            <div className="mb-2 flex items-center gap-2 text-sm font-bold text-[#16233b]"><Barcode className="h-4 w-4 text-teal-600" /> الباركود / رقم القميص</div>
            <form onSubmit={onScanSubmit} className="flex gap-2">
              <input ref={scanRef} value={scanValue} onChange={(e) => setScanValue(e.target.value)} dir="ltr"
                placeholder="امسح الباركود أو أدخل الرقم..."
                className="flex-1 rounded-lg border border-teal-200 bg-white px-3 py-2 text-sm outline-none focus:border-teal-400" data-testid="scan-input" />
              <button type="submit" className="rounded-lg bg-[#16233b] px-4 py-2 text-sm font-semibold text-white transition-transform hover:scale-105" data-testid="scan-submit">تسجيل</button>
            </form>
          </div>

          <button type="button" onClick={() => setCameraOpen(true)} className="group rounded-2xl border-2 border-slate-200 bg-white p-4 text-right shadow-sm transition-all hover:-translate-y-0.5 hover:border-teal-300 hover:shadow-md" data-testid="camera-open-btn">
            <div className="mb-2 flex items-center gap-2 text-sm font-bold text-[#16233b]"><CameraIcon className="h-4 w-4 text-teal-600" /> القراءة بالكاميرا</div>
            <p className="text-xs text-slate-500">افتح الكاميرا لمسح رمز QR من بطاقة اللاعب مباشرة</p>
            <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-teal-700 group-hover:underline">فتح الكاميرا ←</span>
          </button>

          <div className="relative cursor-not-allowed rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 p-4 opacity-70" data-testid="face-recognition-card" title="قريباً — التحضير بالتعرف على الوجه">
            <span className="absolute left-3 top-3 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">قريباً</span>
            <div className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-500"><ScanFace className="h-4 w-4" /> التحضير بالتعرف على الوجه</div>
            <p className="text-xs text-slate-400">تسجيل حضور اللاعبين تلقائياً بمجرد التعرف على وجههم أمام الكاميرا</p>
            <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-slate-400"><Lock className="h-3 w-3" /> غير متاحة بعد</span>
          </div>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-2 rounded-xl bg-slate-50 px-3 py-2">
          <span className="text-xs text-slate-500">أو تسجيل يدوي:</span>
          <select value={manualPlayer} onChange={(e) => setManualPlayer(e.target.value)} className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm" data-testid="manual-player-select">
            <option value="">اختر لاعباً</option>
            {absent.map((p) => <option key={p.id} value={p.id}>{p.name}{p.jersey_number ? ` (${p.jersey_number})` : ''}</option>)}
          </select>
          <button onClick={onManual} disabled={!manualPlayer} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 disabled:opacity-40" data-testid="manual-register-btn">تسجيل حضور</button>
        </div>
        {feedback && (
          <div className={`mt-3 flex items-start gap-3 rounded-xl px-3 py-2.5 ${feedback.type === 'ok' ? 'bg-teal-50' : feedback.type === 'dup' ? 'bg-amber-50' : 'bg-red-50 ring-1 ring-red-200'}`} data-testid="scan-feedback">
            {feedback.player && (
              feedback.player.photo_url ? (
                <img src={feedback.player.photo_url} alt="" className="h-11 w-11 shrink-0 rounded-full border-2 border-white object-cover shadow-sm" data-testid="scan-feedback-photo" onError={(e) => { e.target.style.display = 'none'; }} />
              ) : (
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-white bg-slate-200 text-sm font-bold text-slate-500 shadow-sm">{feedback.player.name?.trim()?.[0] || '؟'}</span>
              )
            )}
            <div className="min-w-0 flex-1">
              <p className={`text-sm font-semibold ${feedback.type === 'ok' ? 'text-teal-800' : feedback.type === 'dup' ? 'text-amber-800' : 'text-red-700'}`}>{feedback.msg}</p>
              <SubscriptionInfo player={feedback.player} />
              {feedback.type === 'confirm' && (
                <div className="mt-2 flex gap-2">
                  <button onClick={confirmForceRegister} className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-red-700" data-testid="scan-force-register-btn">تسجيل رغم الانتهاء</button>
                  <button onClick={dismissFeedback} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50" data-testid="scan-dismiss-btn">تجاهل</button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="attendance-print rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="attendance-print-header hidden border-b border-slate-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-base font-bold text-slate-900">{club?.name || club?.club_name || 'المنشأة الرياضية'} — سجل الحضور</div>
              <div className="text-xs text-slate-500">التاريخ: {day} · الحاضرون: {data.present || 0} من {data.total_active_players || 0} ({pct}%)</div>
            </div>
          </div>
        </div>
        <div className="attendance-print-toolbar flex flex-col gap-2 border-b border-slate-100 px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center">
          <div className="flex gap-2">
            <input type="date" value={day} onChange={(e) => setDay(e.target.value)} className={`${inputCls} flex-1 sm:w-auto sm:flex-none`} data-testid="attendance-date" />
            <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} className="flex-1 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm sm:w-auto sm:flex-none" data-testid="attendance-category-filter">
              <option value="">كل الفئات</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="relative w-full sm:w-44">
            <Search className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="بحث سريع عن لاعب..." className="w-full rounded-lg border border-slate-200 bg-white py-1.5 pr-8 pl-2 text-sm outline-none focus:border-teal-300" data-testid="attendance-search" />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 sm:mr-auto sm:justify-end sm:gap-3">
            <button onClick={() => window.print()} disabled={(data.records || []).length === 0} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:border-teal-300 disabled:opacity-40" data-testid="attendance-print-btn">
              <Icon name="document" className="w-4 h-4" /> طباعة السجل
            </button>
            <button onClick={handleExportPdf} disabled={visibleRecords.length === 0 || pdfExporting} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:border-teal-300 disabled:opacity-40" data-testid="attendance-export-pdf-btn">
              {pdfExporting ? <><span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-teal-600" /> جاري التصدير...</> : <><Icon name="download" className="w-4 h-4" /> PDF</>}
            </button>
            <button onClick={handleExportExcel} disabled={visibleRecords.length === 0} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:border-teal-300 disabled:opacity-40" data-testid="attendance-export-excel-btn">
              <Icon name="download" className="w-4 h-4" /> Excel
            </button>
            <span className="text-xs text-slate-400">{visibleRecords.length} سجل · <span className="font-semibold text-teal-600">{activeAttendees} فعال</span> · <span className="font-semibold text-red-500">{expiredAttendees} منتهي</span></span>
          </div>
        </div>
        {loading ? <div className="space-y-2 p-4">{[1, 2, 3].map((i) => <div key={i} className="h-10 animate-pulse rounded-lg bg-slate-100" />)}</div>
          : visibleRecords.length === 0 ? <div className="p-6"><EmptyState icon="clock" text={q ? 'لا نتائج مطابقة للبحث' : 'لا يوجد حضور مسجل لهذا اليوم'} testId="attendance-empty" /></div>
          : (
            <>
              <div className="divide-y divide-slate-100 sm:hidden">
                {visibleRecords.map((r) => {
                  const subBadge = subscriptionBadge(r);
                  return (
                    <div key={r.id} className={`flex items-center gap-3 px-4 py-3 ${subBadge.key === 'expired' ? 'bg-red-50/40' : ''}`} data-testid="attendance-row-mobile">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-teal-100 text-xs font-bold text-teal-700">{r.player_name?.trim()?.[0] || '؟'}</span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <p className="truncate text-sm font-semibold text-slate-800">{r.player_name}</p>
                          {r.jersey_number != null && <span className="shrink-0 rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-bold text-slate-600">#{r.jersey_number}</span>}
                        </div>
                        <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
                          <span dir="ltr">{r.time}</span>
                          <span className={`rounded-full px-1.5 py-0.5 text-[11px] font-bold ${subBadge.cls}`}>{subBadge.label}</span>
                        </div>
                      </div>
                      <button onClick={() => removeRecord(r.id)} className="shrink-0 rounded p-1.5 text-slate-400 hover:text-red-600" data-testid={`attendance-del-btn-mobile-${r.id}`}><Icon name="trash" className="w-4 h-4" /></button>
                    </div>
                  );
                })}
              </div>
              <div className="hidden overflow-x-auto sm:block">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-slate-100 text-right text-xs text-slate-500">
                    <th className="px-4 py-2 font-semibold">اللاعب</th>
                    <th className="px-2 py-2 font-semibold">الرقم</th>
                    <th className="px-2 py-2 font-semibold">حالة الاشتراك</th>
                    <th className="px-2 py-2 font-semibold">الوقت</th>
                    <th className="px-2 py-2 font-semibold">سجّله</th>
                    <th className="px-2 py-2"></th>
                  </tr></thead>
                  <tbody>
                    {visibleRecords.map((r) => {
                      const subBadge = subscriptionBadge(r);
                      return (
                      <tr key={r.id} className={`border-b border-slate-50 transition-colors hover:bg-slate-50/60 ${subBadge.key === 'expired' ? 'bg-red-50/40' : ''}`} data-testid="attendance-row">
                        <td className="flex items-center gap-2 px-4 py-2.5 font-semibold text-slate-800">
                          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-teal-100 text-[11px] font-bold text-teal-700">{r.player_name?.trim()?.[0] || '؟'}</span>
                          {r.player_name}
                        </td>
                        <td className="px-2 py-2 text-slate-600">{r.jersey_number != null ? <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-xs font-bold">{r.jersey_number}</span> : '—'}</td>
                        <td className="px-2 py-2"><span className={`rounded-full px-2 py-0.5 text-xs font-bold ${subBadge.cls}`} data-testid={`attendance-sub-status-${r.id}`}>{subBadge.label}</span></td>
                        <td className="px-2 py-2 text-slate-600" dir="ltr">{r.time}</td>
                        <td className="px-2 py-2 text-xs text-slate-500">{r.recorded_by}</td>
                        <td className="px-2 py-2 text-left"><button onClick={() => removeRecord(r.id)} className="rounded p-1 text-slate-400 hover:text-red-600" data-testid={`attendance-del-btn-${r.id}`}><Icon name="trash" className="w-4 h-4" /></button></td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
      </div>

      {cameraOpen && <CameraScanner onScan={(code, force) => registerCode(code, force)} onClose={() => setCameraOpen(false)} />}

      <div style={{ position: 'fixed', top: 0, left: '-99999px' }} ref={exportPrintRef}>
        <div style={{ width: '700px', background: '#fff' }}>
          <ReportPrintFrame settings={studioSettings} identity={identity} reportTitle="سجل الحضور والانصراف" exportedByName={user?.full_name}>
            <div className="space-y-3">
              {studioContent.cover_page && (
                <p style={{ fontSize: 12, color: '#334155' }}>التاريخ: {day} {catFilter ? `— الفئة: ${categories.find((c) => c.id === catFilter)?.name || ''}` : ''}</p>
              )}
              {studioContent.stat_cards !== false && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 8 }}><div style={{ fontSize: 16, fontWeight: 800 }}>{data.present || 0}</div><div style={{ fontSize: 10, color: '#64748b' }}>الحاضرون</div></div>
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 8 }}><div style={{ fontSize: 16, fontWeight: 800 }}>{activeAttendees}</div><div style={{ fontSize: 10, color: '#64748b' }}>اشتراك فعال</div></div>
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 8 }}><div style={{ fontSize: 16, fontWeight: 800 }}>{expiredAttendees}</div><div style={{ fontSize: 10, color: '#64748b' }}>اشتراك منتهي</div></div>
                </div>
              )}
              {studioContent.tables !== false && (
                <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                  <thead><tr style={{ background: studioSettings.page?.table_header_color || '#16233b', color: '#fff' }}>
                    <th style={{ padding: '5px 8px', textAlign: 'right' }}>اللاعب</th>
                    <th style={{ padding: '5px 8px', textAlign: 'right' }}>الرقم</th>
                    <th style={{ padding: '5px 8px', textAlign: 'right' }}>حالة الاشتراك</th>
                    <th style={{ padding: '5px 8px', textAlign: 'right' }}>الوقت</th>
                  </tr></thead>
                  <tbody>
                    {visibleRecords.map((r) => (
                      <tr key={r.id} style={{ borderBottom: studioSettings.page?.show_table_borders !== false ? '1px solid #e2e8f0' : 'none' }}>
                        <td style={{ padding: '5px 8px' }}>{r.player_name}</td>
                        <td style={{ padding: '5px 8px' }}>{r.jersey_number ?? '—'}</td>
                        <td style={{ padding: '5px 8px' }}>{subscriptionBadge(r).label}</td>
                        <td style={{ padding: '5px 8px' }} dir="ltr">{r.time}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </ReportPrintFrame>
        </div>
      </div>

      {visibleAbsent.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-2 text-sm font-bold text-slate-700">الغائبون ({visibleAbsent.length})</div>
          <div className="flex flex-wrap gap-1.5">
            {visibleAbsent.slice(0, 40).map((p) => (
              <span key={p.id} className="flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600 transition-colors hover:bg-slate-200" data-testid="absent-chip">
                {p.name}
                {canWhatsApp && (p.guardian_phone || p.phone) && (
                  <button onClick={() => setWaAbsentPlayer(p)} title="مراسلة عبر واتساب" className="text-emerald-600 hover:text-emerald-800" data-testid="absent-whatsapp-btn">
                    <Icon name="whatsapp" className="w-3.5 h-3.5" />
                  </button>
                )}
              </span>
            ))}
            {visibleAbsent.length > 40 && <span className="text-xs text-slate-400">+{visibleAbsent.length - 40}</span>}
          </div>
        </div>
      )}

      {waAbsentPlayer && (
        <WhatsAppMessageModal
          open={Boolean(waAbsentPlayer)}
          onClose={() => setWaAbsentPlayer(null)}
          recipient={{ id: waAbsentPlayer.id, name: waAbsentPlayer.guardian_name || waAbsentPlayer.name, phone: waAbsentPlayer.guardian_phone || waAbsentPlayer.phone, type: 'player_guardian' }}
          variables={{ guardian_name: waAbsentPlayer.guardian_name || 'ولي الأمر', player_name: waAbsentPlayer.name, training_date: day }}
          relatedType="attendance"
          relatedId={waAbsentPlayer.id}
        />
      )}
      <style>{`@media print { body * { visibility: hidden !important; } .attendance-print, .attendance-print * { visibility: visible !important; } .attendance-print { position: fixed !important; inset: 0; border: 0 !important; overflow: visible !important; } .attendance-print-header { display: block !important; } .attendance-print-toolbar { display: none !important; } }`}</style>
    </div>
  );
}
